import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Scale, LineChart, Users, ShieldCheck, Landmark, Handshake, Phone } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const HERO = "https://images.pexels.com/photos/14936005/pexels-photo-14936005.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

export default function Home() {
  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img src={HERO} alt="London financial district at night" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full pt-28">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} className="max-w-3xl">
            <div className="overline mb-6">Expert Advisory · Commercial &amp; Civil Mediation</div>
            <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              Financial insight where <span className="text-silver italic">disputes</span> are decided.
            </h1>
            <p className="text-zinc-400 text-lg mt-8 max-w-xl leading-relaxed">
              Shadow expert financial advice for lawyers, litigators and counsel — and impartial mediation for their clients. Individuals may instruct mediation directly, though legal representation is advised to formalise a robust, binding agreement.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/advisory" data-testid="hero-advisory-btn" className="btn-metal px-8 py-4 rounded-full font-medium text-sm flex items-center gap-2">
                Expert Advisory <ArrowUpRight size={16} />
              </Link>
              <Link to="/mediation" data-testid="hero-mediation-btn" className="px-8 py-4 rounded-full border border-white/20 text-zinc-200 hover:border-white/50 hover:text-white transition-all text-sm flex items-center gap-2">
                Mediation Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-white/10 bg-[#080808]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center md:text-left">
          {[
            { k: "Chartered", v: "Certified Accountant" },
            { k: "International", v: "Accredited Mediator" },
            { k: "Confidential", v: "MFA-secured portal" },
            { k: "Impartial", v: "Balanced approach" },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="font-display text-2xl text-silver">{s.k}</div>
              <div className="text-zinc-500 text-sm mt-1">{s.v}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Two services bento */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-28">
        <Reveal>
          <div className="overline mb-4">Two Disciplines · One Firm</div>
          <h2 className="font-display font-light text-4xl lg:text-5xl max-w-2xl leading-tight">A rare combination of financial depth and dispute resolution.</h2>
        </Reveal>
        <div className="grid lg:grid-cols-2 gap-6 mt-16">
          <Reveal delay={0.05}>
            <Link to="/advisory" className="group block h-full rounded-2xl border border-white/10 bg-[#0d0d0d] p-10 hover:border-white/25 transition-all">
              <LineChart size={30} strokeWidth={1} className="text-zinc-300" />
              <h3 className="font-dash text-2xl mt-8">Expert Financial Advisory</h3>
              <p className="text-zinc-400 mt-4 leading-relaxed">Shadow expert advice to challenge business valuations, resolve shareholder disputes, navigate taxation positions and structure affairs post-resolution — including HNW marital asset division.</p>
              <span className="inline-flex items-center gap-2 text-sm text-zinc-300 mt-8 link-underline group-hover:text-white">Explore advisory <ArrowUpRight size={15} /></span>
            </Link>
          </Reveal>
          <Reveal delay={0.12}>
            <Link to="/mediation" className="group block h-full rounded-2xl border border-white/10 bg-[#0d0d0d] p-10 hover:border-white/25 transition-all">
              <Handshake size={30} strokeWidth={1} className="text-zinc-300" />
              <h3 className="font-dash text-2xl mt-8">Commercial &amp; Civil Mediation</h3>
              <p className="text-zinc-400 mt-4 leading-relaxed">An impartial, balanced mediator with extensive business and financial knowledge — facilitating and supporting the parties in generating options to reach a balanced agreement. Covering marital asset division, sports contractual disputes, commercial disputes between businesses, and other civil matters.</p>
              <span className="inline-flex items-center gap-2 text-sm text-zinc-300 mt-8 link-underline group-hover:text-white">Explore mediation <ArrowUpRight size={15} /></span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Capabilities */}
      <section className="border-t border-white/10 bg-[#080808] py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal><div className="overline mb-4">Where we add value</div>
          <h2 className="font-display font-light text-4xl lg:text-5xl max-w-2xl leading-tight">Instructed by counsel. Trusted by principals.</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-px mt-16 bg-white/10 rounded-2xl overflow-hidden">
            {[
              { icon: Scale, t: "Business Valuations", d: "Challenge, defend and reconcile competing valuation positions with rigorous financial analysis." },
              { icon: Users, t: "Shareholder Disputes", d: "Unpick complex ownership and exit disagreements to support settlement or litigation." },
              { icon: Landmark, t: "Taxation Positions", d: "Assess and structure tax outcomes that withstand scrutiny and protect value." },
              { icon: ShieldCheck, t: "HNW Marital Division", d: "Discreet expert input on asset identification, valuation and equitable division." },
              { icon: LineChart, t: "Post-Resolution Structure", d: "Design the financial architecture that gives agreements longevity and clarity." },
              { icon: Handshake, t: "Facilitated Resolution", d: "Impartial mediation that turns entrenched positions into workable agreements." },
            ].map((c, i) => (
              <Reveal key={c.t} delay={(i % 3) * 0.06} className="bg-[#0b0b0b]">
                <div className="p-8 h-full">
                  <c.icon size={24} strokeWidth={1} className="text-zinc-400" />
                  <h3 className="font-dash text-lg mt-6">{c.t}</h3>
                  <p className="text-zinc-500 text-sm mt-3 leading-relaxed">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-28">
        <Reveal>
          <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#121212] to-[#0a0a0a] p-12 lg:p-20 text-center">
            <div className="overline mb-5">Confidential by design</div>
            <h2 className="font-display font-light text-4xl lg:text-5xl max-w-3xl mx-auto leading-tight">Discuss your matter, or access your secure client portal.</h2>
            <p className="text-zinc-400 mt-6 max-w-xl mx-auto">Existing clients exchange confidential documents and meeting details through a portal protected by multi-factor authentication.</p>
            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <Link to="/contact" data-testid="cta-contact-btn" className="btn-metal px-8 py-4 rounded-full font-medium text-sm">Make an enquiry</Link>
              <a href="tel:+447980285358" data-testid="cta-call-btn" className="px-8 py-4 rounded-full border border-white/20 text-zinc-200 hover:border-white/50 hover:text-white transition-all text-sm flex items-center gap-2"><Phone size={15} strokeWidth={1.5} /> Call 07980 285358</a>
              <Link to="/login" data-testid="cta-portal-btn" className="px-8 py-4 rounded-full border border-white/20 text-zinc-200 hover:border-white/50 transition-all text-sm">Client portal login</Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
